import React, { ReactElement, useState, useCallback, useRef } from 'react';
import { Button, Container, Heading, Text } from 'rsuite';
import { generateAndDownloadRecords } from './taxTemplateBuilder';
import { BucketData, PaymentPeriod, TaxDataModel } from 'src/types/tax';
import { parseSpreadsheetFromFile } from '../../../common/spreadsheetUtils';
import {
  convertRecordsToPeriods,
  validateRecords,
} from './taxTemplateValidator';
import { TemplateRecord, FILE_ERROR, RECORD_ERROR } from './utils';
import { BulkTaxError } from './BulkTaxError';
import { TaxConfigurationDeductions } from 'src/types/configuration';

interface BulkTaxProps {
  bucketPerPeriod: Record<string, Array<BucketData>>;
  deductions?: TaxConfigurationDeductions;
  onSubmit: (data: TaxDataModel) => void;
  periods: Array<PaymentPeriod>;
  step1Description: string;
  step2Description: string;
}

const UPLOAD_FILE_INPUT_ID = 'totReportFileInput';

export const BulkTax = ({
  bucketPerPeriod,
  deductions,
  onSubmit,
  periods,
  step1Description,
  step2Description,
}: BulkTaxProps): ReactElement => {
  const fileUploaderRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Array<RECORD_ERROR>>([]);
  const downloadTemplate = useCallback(() => {
    generateAndDownloadRecords(periods, bucketPerPeriod, deductions?.enable);
  }, [periods, bucketPerPeriod, deductions]);

  const handleUploadButtonClick = useCallback(() => {
    fileUploaderRef.current?.click();
  }, []);

  const uploadReport = useCallback(async () => {
    // Read uploaded file
    setErrors([]);
    if (!fileUploaderRef.current || !fileUploaderRef.current?.files?.[0]) {
      setErrors([FILE_ERROR.SPREADSHEET_FILE]);
      return;
    }
    const file = fileUploaderRef.current?.files?.[0] as File;
    // Reset the file input ti be able to trigger the onChange event
    // if there are any errors on the process
    fileUploaderRef.current.value = '';

    try {
      const unvalidatedRecords: Record<string, string>[] =
        await parseSpreadsheetFromFile(file);

      const { validatedRecords, errors } = await validateRecords(
        unvalidatedRecords as unknown as Array<TemplateRecord>,
        periods,
        bucketPerPeriod,
        deductions?.enable
      );

      if (errors.length) {
        const newValidationErrors = errors.map(
          ({ rowIndex, message }) => `Row ${rowIndex + 2}: ${message}`
        );
        setErrors(newValidationErrors);
        return;
      }

      const taxData = convertRecordsToPeriods(
        validatedRecords,
        periods,
        bucketPerPeriod
      );
      onSubmit(taxData);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrors([error.message]);
      } /* else {
        setErrors([FILE_ERROR.SPREADSHEET_FILE_TYPE_NOT_SUPPORTED]);
      }*/
      return;
    }
  }, [setErrors, onSubmit, bucketPerPeriod, periods, deductions]);

  return (
    <Container>
      <Heading level={2}>Download template and upload your report</Heading>
      <ol>
        <li>
          <Text>{step1Description}</Text>
          <Button
            className="btn-primary sm-100-percent"
            style={{ margin: '16px 0', width: '200px' }}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
        </li>
        <li>
          <Text>{step2Description}</Text>
          <input
            id={UPLOAD_FILE_INPUT_ID}
            aria-label="Upload tax report"
            hidden
            ref={fileUploaderRef}
            type="file"
            onChange={uploadReport}
          />
          <Button
            className="btn-primary sm-100-percent"
            style={{ margin: '16px 0', width: '200px' }}
            onClick={handleUploadButtonClick}
          >
            Upload Tax Report
          </Button>
        </li>
      </ol>
      <BulkTaxError errors={errors} />
    </Container>
  );
};
